import React, { useState, useEffect } from 'react';
import { Modal, Select, Space, Tag, message, Spin } from 'antd';
import { authAxios } from '../utils/session_utils';
import { PlayoutServer, YouTubeLiveStream } from './types';
import { getPlayoutServerStatusIcon, getPlayoutServerStatusColor } from './utils';

const { Option } = Select;

interface AssignPlayoutServerModalProps {
  stream: YouTubeLiveStream | null;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  // All streams — used to hide servers already attached to a different stream so
  // the user can't accidentally reassign (and thereby detach) one that's in use.
  streams: YouTubeLiveStream[];
}

const AssignPlayoutServerModal: React.FC<AssignPlayoutServerModalProps> = ({
  stream,
  open,
  onClose,
  onSave,
  streams
}) => {
  const [servers, setServers] = useState<PlayoutServer[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedServerId, setSelectedServerId] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      loadServers();
      setSelectedServerId(stream?.playout_server_id);
    }
  }, [open, stream]);

  const loadServers = async () => {
    setLoading(true);
    try {
      const response = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/playout-servers`
      });
      setServers(response.data.servers || []);
    } catch (error) {
      console.error('Error loading playout servers:', error);
      message.error('Failed to load playout servers');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!stream) return;

    setSaving(true);
    try {
      if (selectedServerId) {
        // Assign server
        const response = await authAxios({
          method: 'POST',
          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}/assign-playout-server`,
          data: { playout_server_id: selectedServerId }
        });
        
        // Check if a stream was unassigned
        if (response.data.unassignedStream) {
          message.warning(
            `Playout server assigned successfully. Note: The server was automatically unassigned from stream "${response.data.unassignedStream.name}".`,
            6
          );
        } else {
          message.success('Playout server assigned successfully');
        }
      } else if (stream.playout_server_id) {
        // Unassign server
        await authAxios({
          method: 'DELETE',
          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}/unassign-playout-server`
        });
        message.success('Playout server unassigned successfully');
      }
      onSave();
    } catch (error: any) {
      console.error('Error assigning playout server:', error);
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('Failed to assign playout server');
      }
    } finally {
      setSaving(false);
    }
  };


  // Servers already attached to a DIFFERENT stream (serverId -> that stream's name).
  // These are shown greyed-out and non-selectable so the user can see them (and
  // which stream holds them) but can't accidentally reassign — which would detach
  // the other, possibly running, stream. The current stream's own server is
  // excluded, so it stays selectable.
  const attachedElsewhere = new Map<string, string>();
  (streams || []).forEach((s) => {
    if (s.playout_server_id && s.id !== stream?.id) {
      attachedElsewhere.set(s.playout_server_id, s.name);
    }
  });

  return (
    <Modal
      title="Assign Playout Server"
      open={open}
      onOk={handleSave}
      onCancel={onClose}
      confirmLoading={saving}
      okText="Save"
      cancelText="Cancel"
      width={600}
    >
      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <p style={{ marginBottom: '10px' }}>
          <strong>Stream:</strong> {stream?.name}
        </p>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : (
          <>
            <p style={{ marginBottom: '10px' }}>
              <strong>Select Playout Server:</strong>
            </p>
            <Select
              value={selectedServerId}
              onChange={setSelectedServerId}
              style={{ width: '100%' }}
              placeholder="Select a playout server..."
              allowClear
            >
              {servers.map(server => {
                const inUseBy = attachedElsewhere.get(server.id);
                return (
                  <Option key={server.id} value={server.id} disabled={!!inUseBy}>
                    <Space>
                      {getPlayoutServerStatusIcon(server.status)}
                      <span>{server.display_name || server.name}</span>
                      {server.display_name && (
                        <span style={{ fontSize: '11px', color: '#999' }}>({server.name})</span>
                      )}
                      {inUseBy && (
                        <Tag color="warning">in use by {inUseBy}</Tag>
                      )}
                      <Tag color={getPlayoutServerStatusColor(server.status)} style={{ marginLeft: 'auto' }}>
                        {server.status}
                      </Tag>
                      <div style={{ fontSize: '11px', color: '#999', marginLeft: '24px' }}>
                        {server.endpoint_url}
                      </div>
                    </Space>
                  </Option>
                );
              })}
            </Select>

            {attachedElsewhere.size > 0 && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                Greyed-out servers are already assigned to another stream. Unassign there first to reuse one.
              </div>
            )}

            {selectedServerId && (
              <div style={{ marginTop: '10px', padding: '10px', background: '#f5f5f5', borderRadius: '4px' }}>
                {(() => {
                  const selectedServer = servers.find(s => s.id === selectedServerId);
                  if (!selectedServer) return null;
                  
                  return (
                    <div>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <div>
                          <strong>Server:</strong> {selectedServer.display_name || selectedServer.name}
                        </div>
                        {selectedServer.display_name && (
                          <div>
                            <strong>Server Name:</strong> {selectedServer.name}
                          </div>
                        )}
                        <div>
                          <strong>Endpoint:</strong> <code>{selectedServer.endpoint_url}</code>
                        </div>
                        <div>
                          <strong>Status:</strong> <Tag color={getPlayoutServerStatusColor(selectedServer.status)}>{selectedServer.status}</Tag>
                        </div>
                        {selectedServer.description && (
                          <div>
                            <strong>Description:</strong> {selectedServer.description}
                          </div>
                        )}
                        {selectedServer.last_health_check && (
                          <div>
                            <strong>Last Health Check:</strong> {new Date(selectedServer.last_health_check).toLocaleString()}
                          </div>
                        )}
                      </Space>
                    </div>
                  );
                })()}
              </div>
            )}

            {!selectedServerId && stream?.playout_server_id && (
              <div style={{ marginTop: '10px', padding: '10px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: '4px' }}>
                <p style={{ margin: 0, color: '#d46b08' }}>
                  <strong>Note:</strong> Clearing the selection will unassign the current playout server from this stream.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default AssignPlayoutServerModal;





